import type { MarketplaceItem, MarketplaceCategory } from "../types";
import { CATEGORIES, CATEGORY_LABEL_KEYS, CATEGORY_EMOJI } from "../constants";
import { Search, Filter } from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { ItemCard } from "./ItemCard";

interface ItemCatalogGridProps {
  items: MarketplaceItem[];
  total: number;
  isLoading: boolean;
  search: string;
  categoryFilter: MarketplaceCategory | null;
  onSearchChange: (value: string) => void;
  onCategoryFilterChange: (value: MarketplaceCategory | null) => void;
  onEdit: (item: MarketplaceItem) => void;
  onDuplicate: (item: MarketplaceItem) => void;
  onDeactivate: (item: MarketplaceItem) => void;
  /** When provided, each card shows a scope/owner badge (Global or org name). Used by the admin catalog. */
  orgNameById?: Record<string, string>;
}

export function ItemCatalogGrid({
  items,
  total,
  isLoading,
  search,
  categoryFilter,
  onSearchChange,
  onCategoryFilterChange,
  onEdit,
  onDuplicate,
  onDeactivate,
  orgNameById,
}: ItemCatalogGridProps) {
  const { t } = useLocale();

  const scopeBadgeFor = (item: MarketplaceItem): string | null => {
    if (!orgNameById) return null;
    if (item.scope === "GLOBAL") return t("rewardCatalog.badgeGlobal");
    return (
      (item.organizationId && orgNameById[item.organizationId]) ||
      t("rewards.drawer.scopeOrganization")
    );
  };
  return (
    <div className="space-y-4">
      {/* Search + Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("rewards.searchPlaceholder")}
            className="h-9 w-full rounded-md border border-input bg-card pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <select
            value={categoryFilter ?? ""}
            onChange={(e) =>
              onCategoryFilterChange(
                (e.target.value as MarketplaceCategory) || null,
              )
            }
            className="h-9 appearance-none rounded-md border border-input bg-card pl-9 pr-8 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">{t("rewards.allCategories")}</option>
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_EMOJI[cat]} {t(CATEGORY_LABEL_KEYS[cat])}
              </option>
            ))}
          </select>
        </div>
        <span className="text-xs text-muted-foreground">{t("rewardCatalog.count", { count: total })}</span>
      </div>

      {/* Grid */}
      {isLoading && items.length === 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-[280px] animate-pulse rounded-lg border border-border bg-card"
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
          <span className="text-4xl">🎁</span>
          <p className="mt-3 text-sm font-medium text-foreground">
            {t("rewards.emptyTitle")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("rewards.emptyHint")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((item) => (
            <ItemCard
              key={item._id}
              item={item}
              onEdit={onEdit}
              onDuplicate={onDuplicate}
              onDeactivate={onDeactivate}
              scopeBadge={scopeBadgeFor(item)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
